// PricingPage: Authenticated user pricing and plan upgrade page
// Shows current plan vs available upgrades with comparison

import React from 'react'
import { useSubscription } from '@/hooks/useSubscription'
import { useCredits } from '@/hooks/useCredits'
import { PricingPlans } from '../billing/PricingPlans'
import {
  Crown,
  CheckCircle,
  ArrowRight,
  Zap,
  TrendingUp
} from 'lucide-react'

interface PricingPageProps {
  onBackToSubscription?: () => void
}

export function PricingPage({ onBackToSubscription }: PricingPageProps) {
  const { subscription, isLoading, isSubscribed } = useSubscription()
  const { balance } = useCredits()

  const getCurrentPlanInfo = () => {
    if (!subscription) return null

    return {
      id: subscription.status === 'free' || subscription.status === 'trial' ? 'free' : 'paid',
      name: subscription.plan_name || 'Free Trial',
      status: subscription.status || 'trial',
      credits: subscription.credits_per_month || 100,
      balance: balance?.balance || 0
    }
  }

  const currentPlan = getCurrentPlanInfo()

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing options...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Plans & Pricing
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Choose the perfect plan for your needs. Upgrade or downgrade anytime.
        </p>
      </div>

      {/* Current Plan Status */}
      {currentPlan && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
                <Crown className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Current Plan</h3>
                <p className="text-gray-600">Your active subscription</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  currentPlan.status === 'active'
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                }`}>
                  {currentPlan.name}
                </span>
                {currentPlan.status === 'active' && (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {currentPlan.balance} of {currentPlan.credits} credits remaining
              </p>
            </div>
          </div>

          {currentPlan.name === 'Free Trial' && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-yellow-600" />
                <div>
                  <p className="text-yellow-800 font-medium">Ready to unlock more features?</p>
                  <p className="text-yellow-700 text-sm">Upgrade to get more credits, advanced features, and priority support.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upgrade Benefits */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <TrendingUp className="w-6 h-6 text-green-600 mr-2" />
          Why Upgrade?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4">
            <div className="text-2xl font-bold text-blue-600 mb-2">More Credits</div>
            <p className="text-gray-600 text-sm">Process more images with higher monthly credit limits</p>
          </div>
          <div className="text-center p-4">
            <div className="text-2xl font-bold text-purple-600 mb-2">Advanced Features</div>
            <p className="text-gray-600 text-sm">Access premium AI models, batch processing, and more</p>
          </div>
          <div className="text-center p-4">
            <div className="text-2xl font-bold text-green-600 mb-2">Priority Support</div>
            <p className="text-gray-600 text-sm">Get faster response times and dedicated assistance</p>
          </div>
        </div>
      </div>

      {/* Pricing Plans */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          Choose Your Plan
        </h3>
        <PricingPlans
          highlightPlan={currentPlan?.name === 'Free Trial' ? 'professional' : 'business'}
          showTrialBanner={false}
          currentPlanId={currentPlan?.id}
        />
      </div>

      {/* Back to Subscription Link */}
      {onBackToSubscription && (
        <div className="text-center pt-6">
          <button
            onClick={onBackToSubscription}
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Back to Subscription Details
          </button>
        </div>
      )}
    </div>
  )
}

export default PricingPage